// Can 36 ("When she started several helpers for one request, I get one notification when they have all finished, not one
// for each"): a helper is never an entry of its own. Its work stands in the entry of the conversation that asked (the first
// one up its chain that is not a helper): working while any of them works, waiting on the person while any of them waits,
// and finished only once all of them are idle, at the latest of their ends. A helper whose asking conversation is not listed
// stands on its own. Shared by the Desk's strip and notifications and Pocket's home list (Jason, 21 Oct 2026: "one
// conversation list"). Pure.

export interface FoldableConversation {
  readonly sessionId: string;
  readonly state: "idle" | "running" | "waiting";
  readonly updatedAt: number;
  /** The World session of the conversation it was born under (a sub-conversation or a helper). */
  readonly parentSessionId?: string;
  /** A helper Cleo started for that conversation. */
  readonly helper?: boolean;
  /** Can 50: its turn waits on a job on the person's Mac (backend view `macCall`): Cleo is working, not waiting on the person. */
  readonly macCall?: unknown;
}

export interface FoldedRequest<C extends FoldableConversation> {
  readonly conversation: C;
  readonly state: C["state"];
  readonly updatedAt: number;
}

/**
 * One entry per request, in the order given. `leaveOut` names conversations that stand in no entry of their own (the Desk
 * leaves out a copy of an earlier Pocket conversation and one a Desk thread already holds); their helpers still fold into
 * them, and so are not listed either.
 */
export function foldWorldRequests<C extends FoldableConversation>(
  conversations: Iterable<C>,
  leaveOut: (conversation: C) => boolean = () => false,
): ReadonlyArray<FoldedRequest<C>> {
  const all = [...conversations];
  const bySession = new Map(
    all.map((conversation) => [conversation.sessionId, conversation] as const),
  );
  const askingOf = (conversation: C): C | null => {
    let at = conversation;
    for (
      let step = 0;
      step < 32 && at.helper === true && at.parentSessionId !== undefined;
      step += 1
    ) {
      const parent = bySession.get(at.parentSessionId);
      if (parent === undefined) return at === conversation ? null : at;
      at = parent;
    }
    return at === conversation ? null : at;
  };
  const helpersOf = new Map<string, C[]>();
  for (const conversation of all) {
    const asking = conversation.helper === true ? askingOf(conversation) : null;
    if (asking !== null)
      helpersOf.set(asking.sessionId, [...(helpersOf.get(asking.sessionId) ?? []), conversation]);
  }
  const out: Array<FoldedRequest<C>> = [];
  for (const conversation of all) {
    if (leaveOut(conversation)) continue;
    if (conversation.helper === true && askingOf(conversation) !== null) continue;
    const request = [conversation, ...(helpersOf.get(conversation.sessionId) ?? [])];
    // A turn asking the person's Mac (Can 50) is Cleo's work, not a wait on the person: it never reads as an approval.
    const state = request.some(
      (one) => one.state === "running" || (one.state === "waiting" && one.macCall !== undefined),
    )
      ? "running"
      : request.some((one) => one.state === "waiting")
        ? "waiting"
        : "idle";
    out.push({ conversation, state, updatedAt: Math.max(...request.map((one) => one.updatedAt)) });
  }
  return out;
}
